import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type {} from '@redux-devtools/extension'
import type { ActivityRecord } from '@/components/activity/ActivityRow'
import { fetchBidActivity, type ActivityFilters } from '@/lib/activity-api'

// ===================
// Constants
// ===================

const PAGE_SIZE = 50

// ===================
// State & Actions
// ===================

interface ActivityState {
  items: ActivityRecord[]
  total: number
  filters: ActivityFilters
  isLoading: boolean
  /** Tracks the active bidId so setFilter can reload without re-passing it. */
  _bidId: string | null

  /** Fetch page 0 for this bid, replacing any existing items. */
  load: (bidId: string) => Promise<void>
  /** Fetch the next page (offset = items.length) and append. */
  loadMore: (bidId: string) => Promise<void>
  /** Update a single filter key and reload from page 0. */
  setFilter: (key: keyof ActivityFilters, value: string | undefined) => Promise<void>
}

// ===================
// Store
// ===================

export const useActivityStore = create<ActivityState>()(
  devtools(
    (set, get) => ({
      items: [],
      total: 0,
      filters: {},
      isLoading: false,
      _bidId: null,

      async load(bidId: string) {
        const { filters } = get()
        set({ isLoading: true, _bidId: bidId })
        try {
          const result = await fetchBidActivity(bidId, filters, { limit: PAGE_SIZE, offset: 0 })
          set({ items: result.items, total: result.total })
        } finally {
          set({ isLoading: false })
        }
      },

      async loadMore(bidId: string) {
        const { items, filters } = get()
        set({ isLoading: true })
        try {
          const result = await fetchBidActivity(bidId, filters, {
            limit: PAGE_SIZE,
            offset: items.length,
          })
          set((state) => ({
            items: [...state.items, ...result.items],
            total: result.total,
          }))
        } finally {
          set({ isLoading: false })
        }
      },

      async setFilter(key: keyof ActivityFilters, value: string | undefined) {
        const { _bidId } = get()
        const newFilters: ActivityFilters = { ...get().filters, [key]: value }
        if (value === undefined) {
          delete newFilters[key]
        }
        set({ filters: newFilters, items: [], total: 0 })

        if (!_bidId) return

        set({ isLoading: true })
        try {
          const result = await fetchBidActivity(_bidId, newFilters, { limit: PAGE_SIZE, offset: 0 })
          set({ items: result.items, total: result.total })
        } finally {
          set({ isLoading: false })
        }
      },
    }),
    { name: 'activity-store' }
  )
)
